// src/utils/staffEducationManager.js
// 📈 教育履歴管理システム - 長期的な人材育成追跡

import { 
    collection, 
    doc, 
    getDoc, 
    setDoc, 
    addDoc, 
    getDocs, 
    query, 
    where, 
    orderBy, 
    limit,
    writeBatch,
    arrayUnion,
    increment,
    Timestamp
  } from "firebase/firestore";
  import { db } from "../firebase";
  import dayjs from "dayjs";
  import { EDUCATION_TYPES, SKILL_THRESHOLDS } from "./educationUtils";
  
  /**
   * 📊 教育履歴の保存（配置完了後）
   * @param {string} date - 対象日
   * @param {Object} educationResult - 教育配置結果
   */
  export const saveEducationHistory = async (date, educationResult) => {
    try {
      console.log("💾 教育履歴保存開始...");
      
      const educationHistoryRef = collection(db, "educationHistory");
      
      const historyData = {
        date,
        timestamp: Timestamp.now(),
        stats: educationResult.educationStats || {},
        assignments: extractEducationAssignments(educationResult),
        utilizationRate: educationResult.educationStats?.utilizationRate || 0,
        totalOpportunities: educationResult.educationStats?.totalEducationOpportunities || 0,
        metadata: {
          systemVersion: "enhanced_v2",
          processedAt: new Date().toISOString()
        }
      };
      
      await addDoc(educationHistoryRef, historyData);
      
      // 個別スタッフの教育進捗更新
      await updateStaffEducationProgress(educationResult);
      
      console.log("✅ 教育履歴保存完了");
    } catch (error) {
      console.error("教育履歴保存エラー:", error);
    }
  };
  
  /**
   * 👤 個別スタッフの教育進捗更新
   * @param {Object} educationResult - 教育配置結果
   */
  const updateStaffEducationProgress = async (educationResult) => {
    const batch = writeBatch(db);
    const today = dayjs().format("YYYY-MM-DD");
    
    // 教育配置されたスタッフの進捗を更新
    if (educationResult.educationalAssignments) {
      Object.values(educationResult.educationalAssignments).forEach(assignment => {
        const educationAssignment = assignment.educationAssignment;
        if (!educationAssignment) return;
        
        // 学習者の進捗更新
        if (educationAssignment.observer) {
          updateStaffProgress(batch, educationAssignment.observer.employeeId, {
            type: "observation",
            date: today,
            surgery: assignment.surgery?.procedure,
            department: assignment.surgery?.department,
            instructor: educationAssignment.instructor?.employeeId
          });
        }
        
        if (educationAssignment.trainee) {
          updateStaffProgress(batch, educationAssignment.trainee.employeeId, {
            type: "practice",
            date: today,
            surgery: assignment.surgery?.procedure,
            department: assignment.surgery?.department,
            instructor: educationAssignment.instructor?.employeeId || educationAssignment.supervisor?.employeeId
          });
        }
        
        if (educationAssignment.supervisee) {
          updateStaffProgress(batch, educationAssignment.supervisee.employeeId, {
            type: "supervised_independence",
            date: today,
            surgery: assignment.surgery?.procedure,
            department: assignment.surgery?.department,
            supervisor: educationAssignment.supervisor?.employeeId
          });
        }
        
        if (educationAssignment.candidate) {
          updateStaffProgress(batch, educationAssignment.candidate.employeeId, {
            type: "assessment",
            date: today,
            surgery: assignment.surgery?.procedure,
            department: assignment.surgery?.department,
            evaluator: educationAssignment.evaluator?.employeeId
          });
        }
        
        // 指導者の指導実績更新
        if (educationAssignment.instructor) {
          updateInstructorProgress(batch, educationAssignment.instructor.employeeId, {
            type: "instruction",
            date: today,
            surgery: assignment.surgery?.procedure,
            department: assignment.surgery?.department,
            trainee: educationAssignment.trainee?.employeeId
          });
        }
        
        if (educationAssignment.supervisor) {
          updateInstructorProgress(batch, educationAssignment.supervisor.employeeId, {
            type: "supervision",
            date: today,
            surgery: assignment.surgery?.procedure,
            department: assignment.surgery?.department,
            supervisee: educationAssignment.supervisee?.employeeId
          });
        }
        
        if (educationAssignment.evaluator) {
          updateInstructorProgress(batch, educationAssignment.evaluator.employeeId, {
            type: "evaluation",
            date: today,
            surgery: assignment.surgery?.procedure,
            department: assignment.surgery?.department,
            candidate: educationAssignment.candidate?.employeeId
          });
        }
      });
    }
    
    try {
      await batch.commit();
      console.log("✅ スタッフ教育進捗更新完了");
    } catch (error) {
      console.error("スタッフ教育進捗更新エラー:", error);
    }
  };
  
  /**
   * 📚 学習者の進捗更新
   */
  const updateStaffProgress = (batch, staffId, educationRecord) => {
    const progressRef = doc(db, "staffEducationProgress", staffId);
    
    batch.set(progressRef, {
      lastEducationDate: Timestamp.now(),
      educationCount: increment(1),
      recentEducation: arrayUnion({
        ...educationRecord,
        timestamp: Timestamp.now()
      }),
      lastUpdated: Timestamp.now()
    }, { merge: true });
  };
  
  /**
   * 👨‍🏫 指導者の指導実績更新
   */
  const updateInstructorProgress = (batch, instructorId, instructionRecord) => {
    const instructorRef = doc(db, "instructorProgress", instructorId);
    
    batch.set(instructorRef, {
      lastInstructionDate: Timestamp.now(),
      instructionCount: increment(1),
      recentInstructions: arrayUnion({
        ...instructionRecord,
        timestamp: Timestamp.now()
      }),
      lastUpdated: Timestamp.now()
    }, { merge: true });
  };
  
  /**
   * 📊 教育効果レポートの生成
   * @param {string} startDate - 開始日
   * @param {string} endDate - 終了日
   * @returns {Object} 教育効果レポート
   */
  export const generateEducationEffectReport = async (startDate, endDate) => {
    try {
      console.log(`📈 教育効果レポート生成: ${startDate} - ${endDate}`);
      
      const histories = await getDocs(query(
        collection(db, "educationHistory"),
        where("date", ">=", startDate),
        where("date", "<=", endDate),
        orderBy("date", "asc")
      ));
      
      const report = {
        period: { startDate, endDate },
        totalEducationOpportunities: 0,
        averageUtilizationRate: 0,
        educationTypeBreakdown: initializeEducationTypeBreakdown(),
        departmentBreakdown: {},
        skillProgressSummary: {},
        trends: {
          utilizationTrend: [],
          opportunityTrend: [],
          improvementAreas: []
        },
        recommendations: []
      };
      
      let totalUtilization = 0;
      let reportCount = 0;
      
      // データ集計
      histories.docs.forEach(doc => {
        const data = doc.data();
        const stats = data.stats || {};
        
        report.totalEducationOpportunities += stats.totalEducationOpportunities || 0;
        
        if (stats.utilizationRate) {
          totalUtilization += stats.utilizationRate;
          reportCount++;
        }
        
        // 教育タイプ別集計
        Object.keys(report.educationTypeBreakdown).forEach(type => {
          report.educationTypeBreakdown[type] += stats[`${type}Assignments`] || 0;
        });
        
        // トレンドデータ
        report.trends.utilizationTrend.push({
          date: data.date,
          rate: stats.utilizationRate || 0
        });
        
        report.trends.opportunityTrend.push({
          date: data.date,
          count: stats.totalEducationOpportunities || 0
        });
        
        // 部署別集計
        if (data.assignments) {
          data.assignments.forEach(assignment => {
            const dept = assignment.department || "その他";
            if (!report.departmentBreakdown[dept]) {
              report.departmentBreakdown[dept] = 0;
            }
            report.departmentBreakdown[dept]++;
          });
        }
      });
      
      // 平均値計算
      report.averageUtilizationRate = reportCount > 0 ? totalUtilization / reportCount : 0;
      
      // 改善エリアの特定
      report.trends.improvementAreas = identifyImprovementAreas(report);
      
      // 推奨事項生成
      report.recommendations = generateEducationRecommendations(report);
      
      console.log("✅ 教育効果レポート生成完了");
      return report;
    } catch (error) {
      console.error("教育効果レポート生成エラー:", error);
      return null;
    }
  };
  
  /**
   * 👤 個人教育進捗レポートの生成
   * @param {string} staffId - スタッフID
   * @param {number} months - 対象月数
   * @returns {Object} 個人進捗レポート
   */
  export const generateIndividualProgressReport = async (staffId, months = 6) => {
    try {
      console.log(`👤 個人教育進捗レポート生成: ${staffId}`);
      
      const [progressDoc, skillDoc, staffDoc] = await Promise.all([
        getDoc(doc(db, "staffEducationProgress", staffId)),
        getDoc(doc(db, "staffSkillSummary", staffId)),
        getDoc(doc(db, "staffList", staffId))
      ]);
      
      const progressData = progressDoc.exists() ? progressDoc.data() : {};
      const skillData = skillDoc.exists() ? skillDoc.data() : {};
      const staffData = staffDoc.exists() ? staffDoc.data() : {};
      
      // 期間内の教育履歴をフィルタ
      const cutoffDate = dayjs().subtract(months, "month");
      const recentEducation = (progressData.recentEducation || [])
        .filter(edu => dayjs(edu.date).isAfter(cutoffDate))
        .sort((a, b) => dayjs(b.date).diff(dayjs(a.date)));
      
      const report = {
        staff: {
          id: staffId,
          name: `${staffData.lastName || ""} ${staffData.firstName || ""}`.trim(),
          experienceYears: calculateExperienceYears(staffData),
          employeeId: staffData.employeeId
        },
        period: {
          months,
          startDate: cutoffDate.format("YYYY-MM-DD"),
          endDate: dayjs().format("YYYY-MM-DD")
        },
        currentSkillLevel: skillData.combatPower || 0,
        educationHistory: recentEducation,
        educationSummary: {
          totalEducationCount: recentEducation.length,
          observationCount: recentEducation.filter(e => e.type === "observation").length,
          practiceCount: recentEducation.filter(e => e.type === "practice").length,
          supervisionCount: recentEducation.filter(e => e.type === "supervised_independence").length,
          assessmentCount: recentEducation.filter(e => e.type === "assessment").length
        },
        skillImprovement: await calculateSkillImprovement(staffId, months),
        departmentProgress: analyzeDepartmentProgress(recentEducation, skillData),
        nextRecommendations: generateNextEducationRecommendations(skillData, recentEducation),
        growthTrajectory: calculateGrowthTrajectory(recentEducation, skillData)
      };
      
      console.log("✅ 個人教育進捗レポート生成完了");
      return report;
    } catch (error) {
      console.error("個人教育進捗レポート生成エラー:", error);
      return null;
    }
  };
  
  /**
   * 📈 スキル向上度の計算
   */
  const calculateSkillImprovement = async (staffId, months) => {
    try {
      // 現在のスキルデータ
      const currentSkillDoc = await getDoc(doc(db, "staffSkillSummary", staffId));
      if (!currentSkillDoc.exists()) return { improvement: 0, details: [] };
      
      const currentSkills = currentSkillDoc.data();
      
      // TODO: 過去のスキルデータとの比較
      // 現在は簡易実装として、教育履歴から推定
      const progressDoc = await getDoc(doc(db, "staffEducationProgress", staffId));
      const progressData = progressDoc.exists() ? progressDoc.data() : {};
      
      const cutoffDate = dayjs().subtract(months, "month");
      const recentEducation = (progressData.recentEducation || [])
        .filter(edu => dayjs(edu.date).isAfter(cutoffDate));
      
      // 教育回数から推定向上度を計算
      const educationImpact = Math.min(recentEducation.length * 2, 20); // 最大20ポイント
      
      return {
        improvement: educationImpact,
        details: [
          {
            category: "教育参加回数",
            before: 0, // TODO: 過去データから取得
            after: recentEducation.length,
            improvement: recentEducation.length
          }
        ],
        estimatedOnly: true
      };
    } catch (error) {
      console.error("スキル向上度計算エラー:", error);
      return { improvement: 0, details: [] };
    }
  };
  
  /**
   * 🏥 部署別進捗分析
   */
  const analyzeDepartmentProgress = (educationHistory, skillData) => {
    const deptProgress = {};
    
    // 教育履歴から部署別回数を集計
    educationHistory.forEach(edu => {
      const dept = edu.department || "その他";
      if (!deptProgress[dept]) {
        deptProgress[dept] = { educationCount: 0, currentSkill: 0 };
      }
      deptProgress[dept].educationCount++;
    });
    
    // 現在のスキルレベルを追加
    if (skillData.departments) {
      Object.entries(skillData.departments).forEach(([dept, skills]) => {
        if (!deptProgress[dept]) {
          deptProgress[dept] = { educationCount: 0, currentSkill: 0 };
        }
        deptProgress[dept].currentSkill = skills.averageScore || 0;
      });
    }
    
    return deptProgress;
  };
  
  /**
   * 💡 次の教育推奨事項生成
   */
  const generateNextEducationRecommendations = (skillData, educationHistory) => {
    const recommendations = [];
    
    // スキルデータに基づく推奨
    if (skillData.departments) {
      Object.entries(skillData.departments).forEach(([dept, skills]) => {
        const scrubScore = skills.scrubScore || 0;
        const circulatingScore = skills.circulatingScore || 0;
        
        if (scrubScore < SKILL_THRESHOLDS.ADVANCED_MIN) {
          recommendations.push({
            type: "skill_development",
            department: dept,
            role: "scrub",
            priority: scrubScore < 4 ? "high" : "medium",
            description: `${dept}での器械出しスキル向上が必要です（現在レベル: ${scrubScore}）`
          });
        }
        
        if (circulatingScore < SKILL_THRESHOLDS.ADVANCED_MIN) {
          recommendations.push({
            type: "skill_development",
            department: dept,
            role: "circulating",
            priority: circulatingScore < 4 ? "high" : "medium",
            description: `${dept}での外回りスキル向上が必要です（現在レベル: ${circulatingScore}）`
          });
        }
      });
    }
    
    // 教育履歴に基づく推奨
    const recentDepartments = [...new Set(educationHistory.map(e => e.department))];
    if (recentDepartments.length < 3) {
      recommendations.push({
        type: "department_exposure",
        priority: "medium",
        description: "幅広い診療科での経験を積むことを推奨します"
      });
    }
    
    const hasAssessment = educationHistory.some(e => e.type === "assessment");
    if (!hasAssessment && skillData.combatPower > 60) {
      recommendations.push({
        type: "assessment_ready",
        priority: "high",
        description: "独り立ち評価を受ける準備が整っている可能性があります"
      });
    }
    
    return recommendations;
  };
  
  /**
   * 📈 成長軌跡の計算
   */
  const calculateGrowthTrajectory = (educationHistory, skillData) => {
    const monthlyData = {};
    
    // 月別教育回数を集計
    educationHistory.forEach(edu => {
      const month = dayjs(edu.date).format("YYYY-MM");
      if (!monthlyData[month]) {
        monthlyData[month] = { educationCount: 0, types: new Set() };
      }
      monthlyData[month].educationCount++;
      monthlyData[month].types.add(edu.type);
    });
    
    // 時系列データに変換
    const trajectory = Object.entries(monthlyData)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, data]) => ({
        month,
        educationCount: data.educationCount,
        varietyScore: data.types.size,
        cumulativeExperience: 0 // TODO: 累積計算
      }));
    
    // 累積経験値を計算
    let cumulative = 0;
    trajectory.forEach(point => {
      cumulative += point.educationCount * 2; // 教育1回 = 2ポイント
      point.cumulativeExperience = cumulative;
    });
    
    return trajectory;
  };
  
  /**
   * 🔄 補助関数群
   */
  const extractEducationAssignments = (educationResult) => {
    const assignments = [];
    
    if (educationResult.educationalAssignments) {
      Object.entries(educationResult.educationalAssignments).forEach(([surgeryId, assignment]) => {
        const educationAssignment = assignment.educationAssignment;
        if (educationAssignment) {
          assignments.push({
            surgeryId,
            surgery: assignment.surgery?.procedure,
            department: assignment.surgery?.department,
            educationType: educationAssignment.type,
            instructor: educationAssignment.instructor?.employeeId,
            trainee: educationAssignment.trainee?.employeeId || 
                     educationAssignment.observer?.employeeId ||
                     educationAssignment.supervisee?.employeeId ||
                     educationAssignment.candidate?.employeeId,
            educationGoal: educationAssignment.educationGoal
          });
        }
      });
    }
    
    return assignments;
  };
  
  const initializeEducationTypeBreakdown = () => {
    const breakdown = {};
    Object.values(EDUCATION_TYPES).forEach(type => {
      breakdown[type] = 0;
    });
    return breakdown;
  };
  
  const identifyImprovementAreas = (report) => {
    const areas = [];
    
    // 活用率が低い場合
    if (report.averageUtilizationRate < 70) {
      areas.push({
        area: "utilization_rate",
        severity: "medium",
        description: "スタッフ活用率の向上が必要です"
      });
    }
    
    // 特定教育タイプが不足している場合
    const totalEducation = Object.values(report.educationTypeBreakdown).reduce((sum, count) => sum + count, 0);
    if (totalEducation > 0) {
      const observationRatio = report.educationTypeBreakdown[EDUCATION_TYPES.FIRST_OBSERVATION] / totalEducation;
      const practiceRatio = report.educationTypeBreakdown[EDUCATION_TYPES.GUIDED_PRACTICE] / totalEducation;
      
      if (observationRatio > 0.6) {
        areas.push({
          area: "education_balance",
          severity: "low",
          description: "見学中心から実践的な教育への移行を検討してください"
        });
      }
      
      if (practiceRatio < 0.2) {
        areas.push({
          area: "practical_education",
          severity: "medium",
          description: "実践的な教育機会の増加が必要です"
        });
      }
    }
    
    return areas;
  };
  
  const generateEducationRecommendations = (report) => {
    const recommendations = [];
    
    // 活用率改善
    if (report.averageUtilizationRate < 80) {
      recommendations.push({
        type: "improve_utilization",
        priority: "medium",
        description: "教育機会の拡大とスケジュール最適化を検討してください",
        expectedImprovement: "10-15%の活用率向上"
      });
    }
    
    // 教育バランス改善
    const totalEducation = Object.values(report.educationTypeBreakdown).reduce((sum, count) => sum + count, 0);
    if (totalEducation > 0) {
      const practiceRatio = report.educationTypeBreakdown[EDUCATION_TYPES.GUIDED_PRACTICE] / totalEducation;
      if (practiceRatio < 0.3) {
        recommendations.push({
          type: "increase_practice",
          priority: "high",
          description: "実践的な教育配置を増やすことで、スキル向上を促進できます",
          expectedImprovement: "スキル習得速度の30%向上"
        });
      }
    }
    
    // トレンド分析
    if (report.trends.utilizationTrend.length >= 5) {
      const recentTrend = report.trends.utilizationTrend.slice(-5);
      const isDecreasing = recentTrend.every((point, index) => 
        index === 0 || point.rate <= recentTrend[index - 1].rate
      );
      
      if (isDecreasing) {
        recommendations.push({
          type: "trend_reversal",
          priority: "high",
          description: "教育活用率の下降トレンドを改善する必要があります",
          expectedImprovement: "トレンドの反転"
        });
      }
    }
    
    return recommendations;
  };
  
  const calculateExperienceYears = (staff) => {
    if (!staff || !staff.year) return 0;
    
    const entryYear = parseInt(staff.year);
    const currentYear = new Date().getFullYear();
    
    return Math.max(0, currentYear - entryYear);
  };
  
  export default {
    saveEducationHistory,
    generateEducationEffectReport,
    generateIndividualProgressReport
  };